const BRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

export function formatBRL(cents: number): string {
  return BRL.format(cents / 100);
}

/**
 * Datas vindas do SQLite legado não têm fuso (`2026-03-15 14:32:00`). Sem o
 * `Z` explícito o navegador interpretaria como hora local e o horário
 * apareceria deslocado três horas para quem está no Brasil.
 */
export function parseServerDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const iso = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
  const comFuso = /[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(comFuso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DATA_HORA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  dateStyle: 'short',
  timeStyle: 'short',
});

const DATA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  dateStyle: 'short',
});

const DATA_LONGA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

export function formatDateTime(value: string | Date | null): string {
  const d = value instanceof Date ? value : parseServerDate(value);
  return d ? DATA_HORA.format(d) : '—';
}

export function formatDate(value: string | Date | null): string {
  const d = value instanceof Date ? value : parseServerDate(value);
  return d ? DATA.format(d) : '—';
}

export function formatLongDate(value: string | Date | null): string {
  const d = value instanceof Date ? value : parseServerDate(value);
  return d ? DATA_LONGA.format(d) : '—';
}

/** "1h 32min" — usado nos indicadores de tempo focado. */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const horas = Math.floor(total / 3600);
  const minutos = Math.floor((total % 3600) / 60);
  if (horas > 0) return `${horas}h ${String(minutos).padStart(2, '0')}min`;
  if (minutos > 0) return `${minutos}min`;
  return `${total}s`;
}

/** Cronómetro da sessão de estudo, em MM:SS ou HH:MM:SS. */
export function formatClock(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

const NUM = new Intl.NumberFormat('pt-BR');

export function formatNumber(value: number): string {
  return NUM.format(value);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/** Data ISO do dia civil brasileiro — a mesma chave que a API usa. */
const DIA_ISO = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function todayIso(at: Date = new Date()): string {
  return DIA_ISO.format(at);
}

export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Segunda-feira da semana de uma data ISO. */
export function mondayOfIso(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const weekday = (d.getUTCDay() + 6) % 7;
  return addDaysIso(iso, -weekday);
}

export const DIAS_SEMANA = [
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
  'Domingo',
] as const;

export function weekdayOfIso(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return DIAS_SEMANA[(d.getUTCDay() + 6) % 7] as string;
}

export function diaCurto(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

/**
 * O caminho hierárquico da `frente` vem como
 * `Pacote::Matéria::Assunto::Subassunto::Pergunta`. Para exibição só interessam
 * os níveis abaixo da matéria.
 */
export function labelDoCaminho(path: string): string {
  const partes = path
    .split('::')
    .map((p) => p.trim())
    .filter(Boolean);
  return partes.length > 2 ? partes.slice(2).join(' › ') : partes.join(' › ');
}

export function pluralizar(n: number, singular: string, plural: string): string {
  return n === 1 ? singular : plural;
}
